import { Cell, Grid, InfoLabel, Text, VFlow } from 'bold-ui'
import { DateField, SelectField, SimNaoRadioGroupField, TextField } from 'components/form'
import { FichaNotificacaoCasoSuspeitoSelectField } from 'components/form/field/select/FichasNotificacaoCasoSuspeitoSelectField'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useServerTime } from 'hooks/useServerTime'
import { useMemo } from 'react'
import { CidadaoNotificacaoCasoSuspeito } from 'view/atendimentos/detail/soap/finalizacao/components/NotificacaoCasoSuspeitoField'

import { Cid10NotificacaoAgravoModel, metaNotificacaoAgravo } from '../model-notificacaoAgravo'

interface DadosClinicosStepProps {
  cid10s: ReadonlyArray<Cid10NotificacaoAgravoModel>
}

const meta = metaNotificacaoAgravo.dadosClinicos

export function DadosClinicosStep(props: Readonly<DadosClinicosStepProps>) {
  const { cid10s } = props

  const { iniciadoEm, cidadao } = useAtendimentoContext()

  const serverTime = useServerTime().getServerTimeNow()

  const cidadaoData = useMemo<CidadaoNotificacaoCasoSuspeito>(
    () => ({
      id: cidadao.id,
      dataNascimento: cidadao.dataNascimento!,
      sexo: cidadao.sexo,
      isIdentidadeGeneroInformada: !!cidadao.desejaInformarIdentidadeGenero,
    }),
    [cidadao]
  )

  return (
    <VFlow>
      <InfoLabel title='CID-10 do atendimento'>
        <VFlow vSpacing={0}>
          {cid10s.map((cid10) => (
            <Text key={cid10.id}>{`CID-10 ${cid10.codigo} de ${cid10.nome}`}</Text>
          ))}
        </VFlow>
      </InfoLabel>

      <Grid>
        <Cell size={12}>
          <FichaNotificacaoCasoSuspeitoSelectField
            name={meta.agravosSuspeitos}
            label='Doenças/agravos suspeitos notificados'
            placeholder='Selecione outras doenças/agravos suspeitos notificados'
            dataAtendimento={iniciadoEm?.getTime()}
            cidadaoData={cidadaoData}
            multiple
          />
        </Cell>

        <Cell size={12}>
          <FichaNotificacaoCasoSuspeitoSelectField
            name={meta.agravosConfirmados}
            label='Doenças/agravos confirmados notificados'
            dataAtendimento={iniciadoEm?.getTime()}
            cidadaoData={cidadaoData}
            multiple
            required
          />
        </Cell>

        <Cell size={12}>
          {/*
            TODO: a tabela MedDRA de sinais e sintomas ainda não existe no PEC — nenhuma entidade,
            enum ou query. Os itens abaixo são provisórios, até a carga chegar pelo back-end
            (issue #35076).
          */}
          <SelectField<string>
            name={meta.sinaisSintomas}
            label='Sinais e sintomas'
            items={SINAIS_SINTOMAS_PENDENTES}
            itemToString={(item) => item}
            multiple
            required
          />
        </Cell>

        <Cell size={12}>
          <TextField name={meta.outrosSinaisSintomas} label='Se outro, especifique' maxLength={100} />
        </Cell>

        <Cell size={4}>
          <DateField name={meta.dataInicioSintomas} label='Data início de sintomas' maxDate={serverTime} />
        </Cell>
        <Cell size={4}>
          <DateField name={meta.dataDiagnostico} label='Data do diagnóstico' maxDate={serverTime} />
        </Cell>
        <Cell size={4}>
          <DateField name={meta.dataAtendimento} label='Data do atendimento' maxDate={serverTime} />
        </Cell>

        <Cell size={12}>
          <SimNaoRadioGroupField
            name={meta.isDoencaRelacionadaTrabalho}
            label='Doença relacionada ao trabalho?'
            required
          />
        </Cell>

        <Cell size={12}>
          <SimNaoRadioGroupField
            name={meta.isDoencaRelacionadaDesastre}
            label='Doença relacionada ao desastre?'
            required
          />
        </Cell>
      </Grid>
    </VFlow>
  )
}

/** Provisório: substituir pelos itens da tabela MedDRA quando ela existir (#35076). */
const SINAIS_SINTOMAS_PENDENTES: string[] = ['item 1', 'item 2', 'item 3']
